import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import type { WorkOrderSummary, WorkOrderStatus } from '../../types';

interface WorkOrderSummaryCardProps {
  summary?: WorkOrderSummary;
  isLoading?: boolean;
}

const statusLabels: Record<WorkOrderStatus, string> = {
  draft: 'Draft',
  pending: 'Pending',
  confirmed: 'Confirmed',
  in_progress: 'In Progress',
  picking: 'Picking',
  packing: 'Packing',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const statusColors: Record<WorkOrderStatus, string> = {
  draft: 'bg-gray-500',
  pending: 'bg-yellow-500',
  confirmed: 'bg-amber-500',
  in_progress: 'bg-blue-500',
  picking: 'bg-purple-500',
  packing: 'bg-indigo-500',
  completed: 'bg-green-500',
  cancelled: 'bg-red-500',
};

export function WorkOrderSummaryCard({ summary, isLoading }: WorkOrderSummaryCardProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Work Orders</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const byStatus = summary?.by_status || [];
  const overdueCount = summary?.overdue_count || 0;
  const pendingAllocation = summary?.pending_allocation;
  const recentOrders = summary?.recent_orders || [];

  const getCountByStatus = (status: WorkOrderStatus) => {
    const item = byStatus.find((s) => s.status === status);
    return item?.count || 0;
  };

  const activeCount = getCountByStatus('pending') + getCountByStatus('in_progress');

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg">Work Orders</CardTitle>
        <Link
          to="/wms/work-orders"
          className="text-sm text-primary hover:underline"
        >
          View All
        </Link>
      </CardHeader>
      <CardContent>
        {/* Status Counts */}
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {getCountByStatus('pending')}
            </div>
            <div className="text-xs text-muted-foreground">Pending</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {getCountByStatus('in_progress')}
            </div>
            <div className="text-xs text-muted-foreground">In Progress</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {getCountByStatus('completed')}
            </div>
            <div className="text-xs text-muted-foreground">Completed</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${overdueCount > 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
              {overdueCount}
            </div>
            <div className="text-xs text-muted-foreground">Overdue</div>
          </div>
        </div>

        {/* Pending Allocation Alert */}
        {pendingAllocation && pendingAllocation.total_quantity > 0 && (
          <div className="mb-4 p-3 bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800 rounded-lg">
            <div className="text-sm font-medium text-orange-800 dark:text-orange-200">
              {pendingAllocation.item_count} items pending allocation
            </div>
            <div className="text-xs text-orange-600 dark:text-orange-400">
              {pendingAllocation.total_quantity} units need to be assigned
            </div>
          </div>
        )}

        {/* Recent Orders */}
        {recentOrders.length > 0 ? (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">Recent</h4>
            {recentOrders.slice(0, 3).map((order) => (
              <Link
                key={order.id}
                to={`/wms/work-orders/${order.id}`}
                className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50 transition-colors"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm">{order.work_order_number}</span>
                    <Badge className={`${statusColors[order.status]} text-white text-xs`}>
                      {statusLabels[order.status]}
                    </Badge>
                  </div>
                  <div className="text-sm text-muted-foreground truncate">
                    {order.title}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : activeCount === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            <p className="text-sm">No active work orders</p>
            <Link
              to="/wms/work-orders"
              className="text-sm text-primary hover:underline"
            >
              Create one
            </Link>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
